import * as _ from "lodash";
import { string } from "yup";

import { Action } from "../../../actions/actions";
import { HallBaseInfo } from "../../../types/types";
import { getCountryCodeOfHall } from "../../../types/type_operators";
import { ConfigurationsActionTypes } from "../actions/actionTypes";
import { configurationsStore } from "../store/store";

export interface SoundsConfiguration {}

export interface SettingsConfiguration {
    points_system: boolean;
    max_bet: number;
    min_total_bet: number;
    report_time: string;
    auto_cash_out: string;
    game_types: string[];
    //communication_protocols: string[]; // REMOVE COMMUNICATION PROTOCOLS
    bingo?: {
        enable_mr_champion: boolean;
        enable_pig_boom: boolean;
        extra_ball_purchase: number;
        extras_count_towards_bet: boolean;
        max_bet?: number;
    };
    spins?: {
        enable_mr_champion: boolean;
        enable_pig_boom: boolean;
        max_bet?: number;
        min_total_bet?: number;
    };
    lotto?: {
        enable_mr_champion: boolean;
        enable_pig_boom: boolean;
        extra_ball_purchase: number;
        extras_count_towards_bet: boolean;
        max_bet?: number;
        min_total_bet?: number;
    };
    keno?: {
        enable_mr_champion: boolean;
        enable_pig_boom: boolean;
        extra_ball_purchase: number;
        extras_count_towards_bet: boolean;
        max_bet?: number;
    };
    tax: number;
    expirationType: string;
    expirationUnit: string;
    expirationTime: number;

    autoSessionRenew: boolean;
    autoSessionTime: string;
    enableProgGlobal: boolean;
    globalProgServer: boolean;
    globalProgMaster: boolean;
    region?: number | string;
    point_system?: {};
    panels?: {
        show_vlt: boolean;
    };
}

export interface MailSenderConfiguration {
    enabled: boolean;
    mail: {
        use_tls: boolean;
        timeout: number;
        host: string;
        host_port: number;
        protocol: string;
        from: string;
        username: string;
        password: string;
    };
    /*keeping: {
        bad: number;
        queue: number;
        expired: number;
        outbox: number;
    };*/
}

export interface AccountingConfiguration {
    system: number;
    voucher_digits?: number;
    sas_as_report_system?: boolean;
    sas_control_bill_acceptor?: boolean;
    provider: string;
}

export interface GameGroupConfiguration {
    id: string;
    description: string;
}

export interface MegapotConfiguration {
    enabled: boolean;
    address?: string;
    server?: any;
}

export enum ProgressiveType {
    NORMAL = "normal",
    PROPORTIONAL = "proportional",
    FRACTIONATED = "fractionated",
    LEVELS = "levels",
    MULTIPOTS = "multipots",
    JUMBOLINK = "jumbolink",
    REELSTRIKE = "reelstrike",
    EASYLINK = "easylink",
    SAP = "sap",
    LUCKYTOKENSLINK = "luckytokenslink",
}

export const progressiveConfigProperties = {
    ID: "id",
    COIN: "coin",
    TYPE: "type",
    TERMINAL_GROUPS: "terminal_groups",
    REQUIRED: "required",
    MAX_VALUE: "max_value",
    CONTRIBUTION: "contribution",
    INITIAL_VALUE: "initial_value",
    INCREMENT_SOUND_VALUE: "increment_sound_value",
    GLOBAL_INCREMENT_SOUND: "global_increment_sound",
    RESERVE_MAX_VALUE: "reserve_max_value",
    RESERVE_CONTRIBUTION: "reserve_contribution",
    MIN_BET: "min_bet",
    MAX_BET: "max_bet",
    MIN_TOTAL_BET: "min_total_bet",
    LEVELS: "levels",
    NUM_OF_LEVELS: "num_of_levels"
};

/**
 * Base metadata for a jackpot configuration. This is included
 * in all the configurations to be created/edited.
 */
interface BaseProgressiveMetadata {
    id: string;
    coin?: number[];
    type: ProgressiveType;

    terminal_groups: string[];
    game_groups: string[];

    required: string[]; // required fields for each jackpot
}

/**
 * Configuration fields common to all the current jackpot types
 * (Normal, Proportional and Levels).
 */
export interface CommonProgressiveConfigs {
    valid: boolean;
    max_value: number;
    contribution: number;
    initial_value: number;
    increment_sound_value: number;
    global_increment_sound: boolean;
}

export interface ProgressiveConfig extends BaseProgressiveMetadata {
    // Common to both Normal and Proportional
    max_value?: number;
    contribution?: number;
    initial_value?: number;
    increment_sound_value?: number;
    global_increment_sound?: boolean;
    reserve_max_value?: number;
    reserve_contribution?: number;

    min_bet?: number; // Specific to Normal
    max_bet?: number; // Specific to Proportional

    // Specific to Levels
    prizes?: CommonProgressiveConfigs[];
    num_of_levels?: number;

    // Validity in accordance to schema
    valid: boolean;
}

export class NormalProgressiveConfig implements ProgressiveConfig {
    id: string;
    coin: number[];
    type: ProgressiveType;
    valid: boolean;

    terminal_groups: string[];
    game_groups: string[];

    required: string[];

    max_value: number;
    contribution: number;
    initial_value: number;
    increment_sound_value: number;
    global_increment_sound: boolean;

    min_bet: number;
    reserve_max_value: number;
    reserve_contribution: number;
}

export class ProportionalProgressiveConfig implements ProgressiveConfig {
    id: string;
    coin: number[];
    type: ProgressiveType;
    valid: boolean;

    terminal_groups: string[];
    game_groups: string[];

    required: string[];

    max_value: number;
    contribution: number;
    initial_value: number;
    increment_sound_value: number;
    global_increment_sound: boolean;

    max_bet: number;
    reserve_max_value: number;
    reserve_contribution: number;
}

export class FractionatedProgressiveConfig implements ProgressiveConfig {
    id: string;
    //coin: number[];
    type: ProgressiveType;
    valid: boolean;

    terminal_groups: string[];
    game_groups: string[];

    required: string[];

    max_value: number;
    contribution: number;
    initial_value: number;
    increment_sound_value: number;
    global_increment_sound: boolean;

    //max_bet: number;
    //reserve_max_value: number;
    //reserve_contribution: number;
}

export class LevelsProgressiveConfig implements ProgressiveConfig {
    id: string;
    coin: number[];
    type: ProgressiveType;
    valid: boolean;

    terminal_groups: string[];
    game_groups: string[];

    required: string[];

    prizes: CommonProgressiveConfigs[];
    num_of_levels: number;
}

export type SpecialPrizeType = "mr_champion" | "pig_boom";

export interface MrChampionConfiguration {
    type?: "mr_champion";
    id: string;
    coin: string;
    initial_value: number;
    max_value: number;
    contribution: number;
    min_players: number;
    min_sort: number;
    max_sort: number;
    weight_coin_in: number;
    weight_total_bonus_win: number;
    percentage_terminal: number;
    time_continue_eligible_bonus: number;
    time_continue_eligible_extra: number;
    time_continue_eligible_game: number;
    terminal_groups: string[];
    game_groups: string[];
    notice_period: number;
    count_down_period: number;
}

export interface PigBoomConfiguration {
    type?: "pig_boom";
    id: string;
    coin: string;
    show_eligible_bonus_icon: boolean;
    bonus_enable_screen: boolean;
    contribution: number;
    min_players: number;
    start_percentage: number;
    prize_gold_value: number;
    prize_silver_value: number;
    prize_bronze_value: number;
    prize_gold_frequency: number;
    prize_silver_frequency: number;
    prize_bronze_frequency: number;
    number_eligible_games: number;
    time_eligible: number;
    pop_time: number;
    min_pop_time: number;
    time_continue_eligible_game: number;
    time_continue_eligible_extra: number;
    time_continue_eligible_bonus: number;
    weight_coin_in: number;
    weight_total_bonus_win: number;
    percentage_terminal: number;
    hunter_draw_no_win: number;
    time_wait_video: number;
    terminal_groups: string[];
    game_groups: string[];
}

export type SpecialPrizeConfiguration =
    | MrChampionConfiguration
    | PigBoomConfiguration
    | ProgressiveConfig;

export interface TerminalGroupConfiguration {
    id: string;
    name: string;
    description: string;
}

export interface GameGroupConfiguration {
    id: string;
    name: string;
}

export enum GROUP_TYPE {
    TERMINAL = "terminal",
    GAME = "game"
}

export interface GlobalJackpotConfig {
    id: string;
    coin: number[];
    max_value?: number;
    contribution?: number;
    initial_value?: number;
    increment_sound_value?: number;
    global_increment_sound?: boolean;
    min_bet?: number;
    global_jackpot_sound?: boolean;
    terminal_groups: string[];
    game_groups: string[];
    // Validity in accordance to schema
    valid: boolean;
}

export interface Configuration extends HallBaseInfo {
    settings: {
        id?: number;
        schema?: {
            id: number;
        };
        created_on?: string;
        version?: number;
        hall?: number;
        selectedGroupType: GROUP_TYPE;

        settings: {
            game_settings: any;
            sounds: SoundsConfiguration;
            game_rtp_profiles: any[];
            settings: SettingsConfiguration;
            accounting: AccountingConfiguration;
            game_groups: GameGroupConfiguration[];
            special_prizes: SpecialPrizeConfiguration[];
            progressives: ProgressiveConfig[];
            terminal_groups: TerminalGroupConfiguration[];
            mailing_lists: any[];
            global_jackpot: GlobalJackpotConfig;
            email: MailSenderConfiguration;
            megapot: MegapotConfiguration;
        };
    };
}

const emptyConfig: Configuration = {
    id: null,
    name: "",
    tag: "",
    address_line_1: "",
    address_line_2: "",
    subsidiary: {
        id: null,
        description: "",
        company: {
            id: null,
            name: "",
            short_name: ""
        },
        country: {
            id: null,
            code: "",
            name: ""
        }
    },
    active: false,
    created_on: "",

    settings: {
        selectedGroupType: null,
        settings: {
            sounds: null,
            game_rtp_profiles: [],
            settings: null,
            game_settings: [],
            accounting: null,
            game_groups: [],
            special_prizes: [],
            progressives: [],
            terminal_groups: [],
            mailing_lists: null,
            global_jackpot: null,
            email: {
                enabled: false,
                mail: null
                //keeping: null
            },
            megapot: null
        }
    }
};

export function existingConfiguration(
    state: Configuration = emptyConfig,
    action: Action
): Configuration {
    switch (action.type) {
        case ConfigurationsActionTypes.CLEAR_CONFIGURATION:
            return emptyConfig;
        case ConfigurationsActionTypes.GET_CONFIGURATION_SUCCESS:
            return {
                ...state,
                ...action.payload
            };
        case ConfigurationsActionTypes.GET_HALL_BASIC_INFO_SUCCESS:
            const hallInfo = action.payload.hall;
            const serverInfo = action.payload.server;
            const countryCode = getCountryCodeOfHall(hallInfo);
            const accountingConfigs = {
                system: null,
                voucher_digits: null,
                sas_as_report_system: null,
                sas_control_bill_acceptor: null,
                usePromoCredits: null,
                expiration_type: null,
                expiration_time: null,
                provider: null
            };

            /*switch (serverInfo.cashing.name) {
                case "TITO":
                    accountingConfigs.voucher_digits = ["ES", "IE"].includes(
                        countryCode
                    )
                        ? 4
                        : 18;
                    //accountingConfigs.sas_as_report_system = false;
                    break;
                case "SAS":
                    accountingConfigs.usePromoCredits = true;
                    break;
                default:
                    break;
            }*/

            return {
                ...state,
                ...hallInfo,
                ...serverInfo,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        settings: state.settings.settings.settings
                            ? {
                                  ...state.settings.settings.settings,
                                  /*
                                  // REMOVE COMMUNICATION PROTOCOLS
                                  communication_protocols:
                                      countryCode !== "PH" &&
                                      state.settings.settings.settings.communication_protocols.indexOf(
                                          "PH FBM v12"
                                      ) !== -1
                                          ? []
                                          : state.settings.settings.settings
                                                .communication_protocols

                                   */
                              }
                            : null,
                        accounting: state.settings.settings.accounting
                            ? state.settings.settings.accounting
                            : accountingConfigs
                    }
                }
            };
        default:
            return state;
    }
}

export function editingConfiguration(
    state: Configuration = emptyConfig,
    action: Action
): Configuration {
    let cleanSpecialPrizes = [];
    let cleanProgressives = [];

    switch (action.type) {
        case ConfigurationsActionTypes.CLEAR_CONFIGURATION:
            return emptyConfig;
        case ConfigurationsActionTypes.GET_CONFIGURATION_SUCCESS:
            return {
                ...state,
                ...action.payload
            };
        case ConfigurationsActionTypes.GET_HALL_BASIC_INFO_SUCCESS:
            const hallInfo = action.payload.hall;
            const serverInfo = action.payload.server;
            const countryCode = getCountryCodeOfHall(hallInfo);
            const accountingConfigs = {
                system: null,
                voucher_digits: null,
                sas_as_report_system: null,
                sas_control_bill_acceptor: null,
                usePromoCredits: null,
                provider: null
            };

            /*switch (serverInfo.cashing.name) {
                case "TITO":
                    accountingConfigs.voucher_digits = ["ES", "IE"].includes(
                        countryCode
                    )
                        ? 4
                        : 18;
                    //accountingConfigs.sas_as_report_system = false;
                    break;
                case "SAS":
                    //accountingConfigs.usePromoCredits = true;
                    break;
                default:
                    break;
            }*/

            return {
                ...state,
                ...hallInfo,
                ...serverInfo,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        settings: state.settings.settings.settings
                            ? {
                                  ...state.settings.settings.settings,
                                  /*
                                  // REMOVE COMMUNICATION PROTOCOLS
                                  communication_protocols:
                                      countryCode !== "PH" &&
                                      state.settings.settings.settings.communication_protocols.indexOf(
                                          "PH FBM v12"
                                      ) !== -1
                                          ? []
                                          : state.settings.settings.settings
                                                .communication_protocols

                                   */
                              }
                            : null,
                        accounting: state.settings.settings.accounting
                            ? state.settings.settings.accounting
                            : accountingConfigs
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_SETTINGS_CONFIG:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        settings: {
                            ...action.payload.config
                        }
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_ACCOUNTING_CONFIG:
            const conditionalSAS =
                action.payload.config.system === 2
                    ? action.payload.config.sas_as_report_system
                    : undefined;

            const conditionalBillAccept =
                action.payload.config.system === 2 &&
                action.payload.config.sas_as_report_system
                    ? action.payload.config.sas_control_bill_acceptor
                    : undefined;

            const provider =
                action.payload.config.system !== 2
                    ? action.payload.config.provider
                    : undefined;

            let accounting = {
                ...action.payload.config
            };

            if (
                action.payload.config.system === 2 &&
                conditionalSAS !== undefined
            ) {
                accounting["sas_as_report_system"] = conditionalSAS;
            }

            if (
                action.payload.config.system === 2 &&
                conditionalBillAccept !== undefined
            ) {
                accounting["sas_control_bill_acceptor"] = conditionalBillAccept;
            }

            if (action.payload.config.system === 2 && provider !== undefined) {
                accounting["provider"] = provider;
            }

            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        accounting: accounting
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_GROUPS_CONFIG:
            let isGameGroup =
                action.payload.type && action.payload.type == "Game_Group";
            let isTerminalGroup =
                action.payload.type && action.payload.type == "Terminal_Group";

            const { config } = action.payload;
            const groupIds = config.map(gg => gg.id);

            let cleanGlobals = {...state.settings.settings.global_jackpot};
            let cleanSpecials = [...state.settings.settings.special_prizes];
            let cleanProgs = [...state.settings.settings.progressives];

            if(isGameGroup) {
                cleanSpecials = cleanSpecials.length > 0 ? cleanSpecials.map(
                    prize => {
                        if (prize.hasOwnProperty('game_groups') && Array.isArray(prize.game_groups)) {
                            prize.game_groups = prize.game_groups.filter(
                                groupId =>  groupIds.includes(groupId)
                            );
                        }
                        return prize;
                    }
                ) : [];

                cleanGlobals["game_groups"] =
                    cleanGlobals["game_groups"] !== undefined &&
                    cleanGlobals["game_groups"].length > 0
                        ? cleanGlobals["game_groups"].filter(groupId =>  groupIds.includes(groupId))
                        : [];

                cleanProgs = state.settings.settings.progressives.map(
                    progressive => {
                        if (progressive) {
                            if(progressive.hasOwnProperty('game_groups') && Array.isArray(progressive.game_groups)) {
                                progressive.game_groups =
                                    progressive.game_groups.filter(groupId => groupIds.includes(groupId));
                            }
                            if (
                                progressive.type == "multipots" ||
                                progressive.type == "jumbolink" ||
                                progressive.type == "luckytokenslink" ||
                                progressive.type == "levels" ||
                                progressive.type == "reelstrike" ||
                                progressive.type == "easylink" ||
                                progressive.type == "sap"
                            ) {
                                progressive["children"].forEach(child => {
                                    if (child.game_groups != undefined && child.hasOwnProperty('game_groups') && Array.isArray(child.game_groups)) {
                                        child.game_groups =
                                            child.game_groups.filter(
                                                childGroupId => groupIds.includes(childGroupId)
                                            );
                                    }
                                });
                            }
                        }
                        return progressive;
                    }
                );

            }

            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        terminal_groups: isTerminalGroup
                            ? [...action.payload.config]
                            : state.settings.settings.terminal_groups,
                        game_groups: isGameGroup
                            ? [...action.payload.config]
                            : state.settings.settings.game_groups,
                        progressives: cleanProgs,
                        special_prizes: cleanSpecials,
                        global_jackpot: cleanGlobals
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_GROUPS_BASED_ON_GAME_TYPES:
            const { newGroupsState } = action.payload;

            cleanSpecialPrizes = state.settings.settings.special_prizes.map(
                prize => {
                    if (prize.game_groups) {
                        prize.game_groups = prize.game_groups.filter(
                            groupId => {
                                return (
                                    _.findIndex(
                                        newGroupsState.gameGroups,
                                        gg => {
                                            return gg.id == groupId;
                                        }
                                    ) > -1
                                );
                            }
                        );
                    }
                    return prize;
                }
            );

            cleanProgressives = state.settings.settings.progressives.map(
                progressive => {
                    if (progressive) {
                        if(progressive.game_groups) {
                            progressive.game_groups =
                                progressive.game_groups.filter(groupId => {
                                    return (
                                        _.findIndex(
                                            newGroupsState.gameGroups,
                                            gg => {
                                                return gg.id == groupId;
                                            }
                                        ) > -1
                                    );
                                });
                        }
                        if (
                            progressive.type == "multipots" ||
                            progressive.type == "jumbolink" ||
                            progressive.type == "luckytokenslink" ||
                            progressive.type == "levels" ||
                            progressive.type == "reelstrike" ||
                            progressive.type == "easylink" ||
                            progressive.type == "sap"
                        ) {
                            progressive["children"].forEach(child => {
                                if (child.game_groups != undefined) {
                                    child.game_groups =
                                        child.game_groups.filter(
                                            childGroupId => {
                                                return (
                                                    _.findIndex(
                                                        newGroupsState.gameGroups,
                                                        gg => {
                                                            return (
                                                                gg.id ==
                                                                childGroupId
                                                            );
                                                        }
                                                    ) > -1
                                                );
                                            }
                                        );
                                }
                            });
                        }
                    }
                    return progressive;
                }
            );

            let cleanGlobalJackpots = {
                ...state.settings.settings.global_jackpot
            };
            cleanGlobalJackpots["game_groups"] =
                cleanGlobalJackpots["game_groups"] !== undefined &&
                cleanGlobalJackpots["game_groups"].length > 0
                    ? cleanGlobalJackpots["game_groups"].filter(groupId => {
                          return (
                              _.findIndex(newGroupsState.gameGroups, gg => {
                                  return gg.id == groupId;
                              }) > -1
                          );
                      })
                    : [];

            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        game_groups: state.settings.settings.game_groups,
                        progressives: cleanProgressives,
                        special_prizes: cleanSpecialPrizes,
                        global_jackpot: cleanGlobalJackpots
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_SPECIAL_PRIZES_BASED_ON_BINGO_SETTINGS:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        special_prizes: [...action.payload.newPrizesState]
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_PROGRESSIVES_CONFIG:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        progressives: [...action.payload.config]
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_MAILING_CONFIG:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        mailing_lists:
                            action.payload &&
                            action.payload.list.lists &&
                            action.payload.list.lists.length > 0
                                ? [...action.payload.list.lists]
                                : []
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_SPECIAL_PRIZES_CONFIG:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        special_prizes: [...action.payload.config]
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_MAIL_SENDER_CONFIG:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        email: { ...action.payload.config }
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_GLOBAL_JACKPOT_CONFIG:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        global_jackpot: {
                            ...action.payload.config
                        }
                    }
                }
            };
        case ConfigurationsActionTypes.UPDATE_MEGAPOT_CONFIG:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        megapot: {
                            ...action.payload.config
                        }
                    }
                }
            };
        case ConfigurationsActionTypes.SELECT_GROUP_TYPE:
            return {
                ...state,
                settings: {
                    ...state.settings,
                    selectedGroupType: action.payload.groupType
                }
            };
        case ConfigurationsActionTypes.CLEAN_GROUP_TYPES:
            let isTerminal =
                state.settings.selectedGroupType !== null &&
                state.settings.selectedGroupType == "terminal";
            let isGame =
                state.settings.selectedGroupType !== null &&
                state.settings.selectedGroupType == "game";

            let cleanGroupSpecialPrizes =
                state.settings.settings.special_prizes;
            let cleanGroupProgressives = state.settings.settings.progressives;

            let didChangeProgressives = false;
            let didChangeSpecialPrizes = false;

            // CLEAN PROGRESSIVES
            if (state.settings.settings) {
                if (isGame) {
                    cleanGroupSpecialPrizes = cleanGroupSpecialPrizes
                        ? cleanGroupSpecialPrizes.map(prize => {
                              if (
                                  prize.terminal_groups &&
                                  prize.terminal_groups.length > 0
                              ) {
                                  prize.terminal_groups = [];
                                  didChangeSpecialPrizes = true;
                              }
                              return prize;
                          })
                        : cleanGroupSpecialPrizes;

                    cleanGroupProgressives = cleanGroupProgressives
                        ? cleanGroupProgressives.map(progressive => {
                              if (
                                  progressive.terminal_groups &&
                                  progressive.terminal_groups.length > 0
                              ) {
                                  progressive.terminal_groups = [];
                                  didChangeProgressives = true;
                              }

                              if (
                                  progressive.type === "levels" ||
                                  progressive.type === "multipots" ||
                                  progressive.type === "jumbolink" ||
                                  progressive.type === "luckytokenslink" ||
                                  progressive.type == "reelstrike" ||
                                  progressive.type === "easylink" ||
                                  progressive.type === "sap"
                              ) {
                                  progressive["children"] = progressive[
                                      "children"
                                  ].map(child => {
                                      if (
                                          child.hasOwnProperty(
                                              "terminal_groups"
                                          ) &&
                                          child.terminal_groups !== null &&
                                          child.terminal_groups !== undefined
                                      ) {
                                          if (
                                              child.terminal_groups.length > 0
                                          ) {
                                              didChangeProgressives = true;
                                              child.terminal_groups = [];
                                          }
                                      }
                                      return child;
                                  });
                              }

                              return progressive;
                          })
                        : cleanGroupProgressives;

                    const removeGroupsSpec = removeExcludedGameGroups(
                        state.settings.settings.game_groups,
                        cleanGroupSpecialPrizes
                    );
                    cleanGroupSpecialPrizes = removeGroupsSpec["result"];
                    if (removeGroupsSpec["didChange"] == true) {
                        didChangeSpecialPrizes = true;
                    }
                    const removeGroupsProg = removeExcludedGameGroups(
                        state.settings.settings.game_groups,
                        cleanGroupProgressives
                    );
                    cleanGroupProgressives = removeGroupsProg["result"];
                    if (removeGroupsProg["didChange"] == true) {
                        didChangeProgressives = true;
                    }
                } else if (isTerminal) {
                    cleanGroupSpecialPrizes = cleanGroupSpecialPrizes.map(
                        prize => {
                            if (
                                prize.game_groups &&
                                prize.game_groups.length > 0
                            ) {
                                prize.game_groups = [];
                                didChangeSpecialPrizes = true;
                            }
                            return prize;
                        }
                    );

                    cleanGroupProgressives = cleanGroupProgressives.map(
                        progressive => {
                            if (
                                progressive.game_groups &&
                                progressive.game_groups.length > 0
                            ) {
                                progressive.game_groups = [];
                                didChangeProgressives = true;
                            }

                            if (
                                progressive.type === "levels" ||
                                progressive.type === "multipots" ||
                                progressive.type === "jumbolink" ||
                                progressive.type === "luckytokenslink" ||
                                progressive.type == "reelstrike" ||
                                progressive.type === "easylink" ||
                                progressive.type === "sap"
                            ) {
                                progressive["children"] = progressive[
                                    "children"
                                ].map(child => {
                                    if (
                                        child.hasOwnProperty("game_groups") &&
                                        child.game_groups !== null &&
                                        child.game_groups !== undefined
                                    ) {
                                        if (child.game_groups.length > 0) {
                                            child.game_groups = [];
                                            didChangeProgressives = true;
                                        }
                                    }
                                    return child;
                                });
                            }

                            return progressive;
                        }
                    );

                    const removeGroupsSpec = removeExcludedTerminalGroups(
                        state.settings.settings.terminal_groups,
                        cleanGroupSpecialPrizes
                    );
                    cleanGroupSpecialPrizes = removeGroupsSpec.result;
                    if (removeGroupsSpec["didChange"] == true) {
                        didChangeSpecialPrizes = true;
                    }
                    const removeGroupsProg = removeExcludedTerminalGroups(
                        state.settings.settings.terminal_groups,
                        cleanGroupProgressives
                    );
                    cleanGroupProgressives = removeGroupsProg["result"];

                    if (removeGroupsProg["didChange"] == true) {
                        didChangeProgressives = true;
                    }
                }
            }

            if (didChangeProgressives) {
                setTimeout(() => {
                    configurationsStore.dispatch({
                        type: ConfigurationsActionTypes.CLEAR_VALID_PROG_SECTION
                    });
                }, 50);
            }

            if (didChangeSpecialPrizes) {
                setTimeout(() => {
                    configurationsStore.dispatch({
                        type: ConfigurationsActionTypes.CLEAR_VALID_SPECIAL_SECTION
                    });
                }, 50);
            }

            return {
                ...state,
                settings: {
                    ...state.settings,
                    settings: {
                        ...state.settings.settings,
                        progressives: cleanGroupProgressives,
                        special_prizes: cleanGroupSpecialPrizes
                    }
                }
            };
        default:
            return state;
    }
}

/**
 * Auxiliary function that excludes no longer existing Terminal Groups from
 * already existing Progressive or Special Prize configurations.
 * @param existingTerminalGroups - The current terminal groups
 * @param configs - The array of configurations (either array of Special Prizes
 * or array of Progressives).
 */
function removeExcludedTerminalGroups(
    existingTerminalGroups: TerminalGroupConfiguration[],
    configs = []
) {
    const terminalGroupIDs: string[] =
        existingTerminalGroups && existingTerminalGroups.length > 0
            ? existingTerminalGroups.map(tg => tg.id)
            : [];
    let didChange = false;
    let result = configs.map(pg => {
        const initialCount =
            pg.terminal_groups == null ? 0 : pg.terminal_groups.length;
        pg.terminal_groups =
            pg.terminal_groups == null
                ? pg.terminal_groups
                : pg.terminal_groups.filter(tg =>
                      terminalGroupIDs.includes(tg)
                  );
        const updatedCount =
            pg.terminal_groups == null ? 0 : pg.terminal_groups.length;

        if (updatedCount > initialCount) {
            didChange = true;
        }

        if (
            pg.type === "levels" ||
            pg.type === "multipots" ||
            pg.type === "jumbolink" ||
            pg.type === "luckytokenslink" ||
            pg.type === "reelstrike" ||
            pg.type === "easylink" ||
            pg.type === "sap"
        ) {
            pg.children = pg["children"].map(child => {
                if (
                    child.hasOwnProperty("terminal_groups") &&
                    child.terminal_groups !== null &&
                    child.terminal_groups !== undefined
                ) {
                    const initialChildCount =
                        child.terminal_groups == null ||
                        child.terminal_groups == undefined
                            ? 0
                            : child.terminal_groups.length;
                    child.terminal_groups = child.terminal_groups.filter(tg =>
                        terminalGroupIDs.includes(tg)
                    );
                    const updatedChildCount =
                        child.terminal_groups == null ||
                        child.terminal_groups == undefined
                            ? 0
                            : child.terminal_groups.length;

                    if (updatedChildCount > initialChildCount) {
                        didChange = true;
                    }
                }
                return child;
            });
        }
        return pg;
    });

    return {
        result,
        didChange
    };
}

/**
 * Auxiliary function that excludes no longer existing Game Groups from
 * already existing Progressive or Special Prize configurations.
 * @param existingGameGroups - The current game groups
 * @param configs - The array of configurations (either array of Special Prizes
 * or array of Progressives).
 */
function removeExcludedGameGroups(
    existingGameGroups: GameGroupConfiguration[],
    configs = []
) {
    const gameGroupIDs: string[] =
        existingGameGroups && existingGameGroups.length > 0
            ? existingGameGroups.map(tg => tg.id)
            : [];

    let didChange = false;
    let result = configs.map(pg => {
        const initialCount = pg.game_groups == null ? 0 : pg.game_groups.length;

        pg.game_groups =
            pg.game_groups == null
                ? pg.game_groups
                : pg.game_groups.filter(tg => gameGroupIDs.includes(tg));

        const updatedCount = pg.game_groups == null ? 0 : pg.game_groups.length;

        if (updatedCount > initialCount) {
            didChange = true;
        }

        if (
            pg.type === "levels" ||
            pg.type === "multipots" ||
            pg.type === "jumbolink" ||
            pg.type === "luckytokenslink" ||
            pg.type === "reelstrike" ||
            pg.type === "easylink" ||
            pg.type === "sap"
        ) {
            pg.children = pg["children"].map(child => {
                if (
                    child.hasOwnProperty("game_groups") &&
                    child.game_groups !== null &&
                    child.game_groups !== undefined
                ) {
                    const initialChildCount =
                        child.game_groups == null ||
                        child.game_groups == undefined
                            ? 0
                            : child.game_groups.length;
                    child.game_groups = child.game_groups.filter(tg =>
                        gameGroupIDs.includes(tg)
                    );

                    const updatedChildCount =
                        child.game_groups == null ||
                        child.game_groups == undefined
                            ? 0
                            : child.game_groups.length;

                    if (updatedChildCount > initialChildCount) {
                        didChange = true;
                    }
                }
                return child;
            });
        }

        return pg;
    });

    return {
        result,
        didChange
    };
}

export function editingConfigurationLoading(
    state: boolean = false,
    action: Action
): boolean {
    switch (action.type) {
        case ConfigurationsActionTypes.GET_CONFIGURATION:
            return true;
        case ConfigurationsActionTypes.GET_CONFIGURATION_SUCCESS:
        case ConfigurationsActionTypes.GET_CONFIGURATION_FAIL:
            return false;
        default:
            return state;
    }
}

export function submitingConfigurationLoading(
    state: boolean = false,
    action: Action
): boolean {
    switch (action.type) {
        case ConfigurationsActionTypes.CREATE_CONFIGURATION:
        case ConfigurationsActionTypes.EDIT_CONFIGURATION:
            return true;
        case ConfigurationsActionTypes.CREATE_CONFIGURATION_SUCCESS:
        case ConfigurationsActionTypes.CREATE_CONFIGURATION_FAIL:
        case ConfigurationsActionTypes.EDIT_CONFIGURATION_SUCCESS:
        case ConfigurationsActionTypes.EDIT_CONFIGURATION_FAIL:
            return false;
        default:
            return state;
    }
}

export function submitingConfigurationState(
    state: {
        error: boolean;
        success: boolean;
        message: string | null;
    } | null = null,
    action: Action
): {
    error: boolean;
    success: boolean;
    message: string | null;
} | null {
    switch (action.type) {
        case ConfigurationsActionTypes.CLEAR_CONFIGURATION:
        case ConfigurationsActionTypes.CLEAR_CONFIGURATION_STATE:
            return null;
        case ConfigurationsActionTypes.CREATE_CONFIGURATION_SUCCESS:
        case ConfigurationsActionTypes.EDIT_CONFIGURATION_SUCCESS:
            return {
                error: false,
                success: true,
                message: null
            };
        case ConfigurationsActionTypes.CREATE_CONFIGURATION_FAIL:
        case ConfigurationsActionTypes.EDIT_CONFIGURATION_FAIL:
            return {
                error: true,
                success: false,
                message: action.payload.text ? action.payload.text : null
            };
        default:
            return state;
    }
}

export function uploadedConfiguration(
    state: any | null = null,
    action: Action
): any | null {
    switch (action.type) {
        case ConfigurationsActionTypes.SET_UPLOAD_CONFIGURATION:
            return action.payload;
        default:
            return state;
    }
}
